<template>
  <div class="modal-content showlabeleditor">
    <div class="modal-header">
      <h4 class="modal-title">{{ labelId ? _('Edit show label') : _('New show label') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-sm-8">
          <div class="form-group">
            <label for="showLabelName">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="showLabelName" v-model="name" :disabled="loading" v-on:keydown.enter.prevent="save" />
          </div>
        </div>
        <div class="col-sm-4" v-if="labelId">
          <label>{{ _('Language') }}</label>
          <VersionSelector :value="editVersion" v-on:input="changeVersion" />
        </div>
      </div>
      <p class="help-block" v-if="labelId">{{ _('Customers see the name in their own language. A language without a translation shows the name in the default language.') }}</p>
      <div class="form-group">
        <label>{{ _('Color') }}</label>
        <div class="show-label-colors">
          <button v-for="c in colors" :key="c" type="button" class="show-label-color" :class="{ selected: color === c }" :style="{ backgroundColor: c }" :title="c" :aria-pressed="color === c ? 'true' : 'false'" v-on:click="color = c"></button>
        </div>
      </div>
      <div class="form-group">
        <label>{{ _('Preview') }}</label>
        <div><span class="show-label-preview" :style="previewStyle">{{ name || _('Name') }}</span></div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" :disabled="!name.trim() || saving || loading" v-on:click="save"><i class="fa fa-fw fa-save"></i> {{ _('Save') }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors.js'
import { SHOW_LABEL_COLORS, SHOW_LABEL_DEFAULT_COLOR, showLabelStyle } from '~/lib/showLabels'
import { VersionSelector } from '#components'

export default {
  props: ['baseDomain', 'shopId', 'apiKey', 'labelId'],
  emits: ['close', 'saved'],
  mixins: [errors],
  data () {
    return {
      name: '',
      color: SHOW_LABEL_DEFAULT_COLOR,
      editVersion: null,
      loading: false,
      saving: false,
      colors: SHOW_LABEL_COLORS
    }
  },
  computed: {
    ...mapGetters(['version']),
    previewStyle () {
      return showLabelStyle({ color: this.color })
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    versionParam () {
      return this.editVersion && this.editVersion !== 'default' ? '&version=' + this.editVersion : ''
    },
    async load () {
      this.loading = true
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/showlabels/' + this.labelId + '.json?apikey=' + this.apiKey + this.versionParam())
      } catch (e) {
        this.loading = false
        return this.showError(e)
      }
      this.loading = false
      this.name = response.data.name || ''
      this.color = response.data.color || SHOW_LABEL_DEFAULT_COLOR
    },
    changeVersion (version) {
      this.editVersion = version
      this.load()
    },
    async save () {
      const name = this.name.trim()
      if (!name || this.saving) {
        return
      }
      this.saving = true
      const data = { name, color: this.color }
      if (this.editVersion && this.editVersion !== 'default') {
        data.version = this.editVersion
      }
      let id = this.labelId
      try {
        if (id) {
          data.id = id
          await axios.put(this.baseDomain + this.shopId + '/showlabels/' + id + '.json?apikey=' + this.apiKey, data)
        } else {
          const response = await axios.post(this.baseDomain + this.shopId + '/showlabels.json?apikey=' + this.apiKey, data)
          id = parseInt(response.data)
        }
      } catch (e) {
        this.saving = false
        return this.showError(e)
      }
      this.saving = false
      this.$emit('saved', id)
      this.hideModal()
    }
  },
  components: {
    VersionSelector
  },
  mounted () {
    this.editVersion = this.version
    if (this.labelId) {
      this.load()
    }
  }
}
</script>

<style>
.show-label-colors {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.show-label-color {
  width: 28px;
  height: 28px;
  border: 2px solid transparent;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #ccc;
}
.show-label-color.selected {
  border-color: #fff;
  box-shadow: 0 0 0 2px #333;
}
.show-label-preview {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}
</style>
